import { StrictMode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline, responsiveFontSizes } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import Hero from './components/Hero';
import Experience from './components/Experience';
import Projects from './components/Projects';
import Skills from './components/Skills';
import Contact from './components/Contact';
import PageTransition from './components/PageTransition';
import BackgroundEffect from './components/BackgroundEffect';

let theme = createTheme({
  palette: {
    primary: {
      main: '#ffffff',
      light: '#ffffff',
      dark: '#e2e8f0',
    },
    secondary: {
      main: '#ffffff',
      light: '#ffffff',
      dark: '#e2e8f0',
    },
    background: {
      default: '#0a1415',
      paper: 'rgba(255, 255, 255, 0.03)',
    },
    text: {
      primary: '#ffffff',
      secondary: '#a1a1aa',
    },
    mode: 'dark',
  },
  typography: {
    fontFamily: '"Inter", "Outfit", sans-serif',
    h1: {
      fontWeight: 700,
      fontFamily: '"Outfit", sans-serif',
      fontSize: '4.5rem',
    },
    h2: {
      fontWeight: 700,
      fontFamily: '"Outfit", sans-serif',
      fontSize: '3.5rem',
    },
    h3: {
      fontWeight: 700,
      fontFamily: '"Outfit", sans-serif',
      fontSize: '3.5rem',
    },
    h4: {
      fontWeight: 600,
      fontFamily: '"Outfit", sans-serif',
      fontSize: '2.5rem',
    },
    h5: {
      fontWeight: 500,
      fontFamily: '"Outfit", sans-serif',
      fontSize: '1.8rem',
    },
    h6: {
      fontWeight: 500,
      fontFamily: '"Outfit", sans-serif',
      fontSize: '1.5rem',
    },
    body1: {
      fontSize: '1.2rem',
      lineHeight: 1.7,
    },
    body2: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0a1415',
          transition: 'background-color 0.2s ease',
          overflowX: 'hidden'
        }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          padding: '8px 16px',
          fontWeight: 500,
        },
      },
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
  },
  shape: {
    borderRadius: 8,
  },
  spacing: 8,
});

// Make typography responsive
theme = responsiveFontSizes(theme);

const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageTransition><Hero /></PageTransition>} />
        <Route path="/experience" element={<PageTransition><Experience /></PageTransition>} />
        <Route path="/projects" element={<PageTransition><Projects /></PageTransition>} />
        <Route path="/skills" element={<PageTransition><Skills /></PageTransition>} />
        <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  return (
    <StrictMode>
        <MuiThemeProvider theme={theme}>
          <CssBaseline />
          <Router>
            <BackgroundEffect />
            <Header />
            <AnimatedRoutes />
          </Router>
        </MuiThemeProvider>
    </StrictMode>
  );
}

export default App;